import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { usePQC } from '../hooks/usePQC';
import { useAuth } from '../contexts/AuthContext';
import { 
  Lock, 
  Unlock, 
  FileText, 
  Upload, 
  Download, 
  Copy, 
  Eye, 
  EyeOff, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw,
  Key,
  Zap,
  Clock,
  FileCheck,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

interface PQCCryptoInterfaceProps {
  className?: string;
}

interface EncryptionResult {
  encryptedData: string;
  timestamp: string;
  algorithm: string;
  size: number;
}

interface DecryptionResult {
  decryptedData: string;
  timestamp: string;
  isSignatureValid?: boolean;
}

const PQCCryptoInterface: React.FC<PQCCryptoInterfaceProps> = ({ className }) => {
  const { user } = useAuth();
  const {
    hasValidKeys,
    encryptData,
    decryptData,
    encryptAndSign,
    decryptAndVerify,
    isEncrypting,
    isDecrypting,
    getPublicKeys
  } = usePQC();

  const [activeTab, setActiveTab] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [inputText, setInputText] = useState('');
  const [recipientPublicKey, setRecipientPublicKey] = useState('');
  const [encryptedInput, setEncryptedInput] = useState('');
  const [encryptionResult, setEncryptionResult] = useState<EncryptionResult | null>(null);
  const [decryptionResult, setDecryptionResult] = useState<DecryptionResult | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [useSignature, setUseSignature] = useState(true);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Limpar estado ao trocar de aba
  useEffect(() => {
    // Limpar resultados e campos de entrada ao trocar de aba
    setShowResults(false);
    setEncryptionResult(null);
    setDecryptionResult(null);
    setInputText('');
    setEncryptedInput('');
    setRecipientPublicKey('');
    setShowSensitiveData(false);
  }, [activeTab]);

  const publicKeys = getPublicKeys();

  const handleEncrypt = async () => {
    if (!inputText.trim()) {
      toast.error('Digite o texto a ser criptografado');
      return;
    }

    if (!recipientPublicKey.trim()) {
      toast.error('Insira a chave pública do destinatário');
      return;
    }

    try {
      let result;
      if (useSignature) {
        result = await encryptAndSign(inputText, recipientPublicKey);
      } else {
        result = await encryptData(inputText, recipientPublicKey);
      }

      if (result) {
        const encryptionResult: EncryptionResult = {
          encryptedData: JSON.stringify(result),
          timestamp: new Date().toISOString(),
          algorithm: 'Kyber + AES-256' + (useSignature ? ' + Dilithium' : ''),
          size: JSON.stringify(result).length
        };
        
        setEncryptionResult(encryptionResult);
        setShowResults(true);
        setInputText('');
      }
    } catch (error) {
      console.error('Erro na criptografia:', error);
      toast.error('Erro ao criptografar dados');
    }
  };

  const handleDecrypt = async () => {
    if (!encryptedInput.trim()) {
      toast.error('Insira os dados criptografados');
      return;
    }

    try {
      const encryptedData = JSON.parse(encryptedInput);
      let result;
      
      if (useSignature) {
        const decryptResult = await decryptAndVerify(encryptedData);
        if (decryptResult) {
          result = {
            decryptedData: decryptResult.data,
            isSignatureValid: decryptResult.isSignatureValid
          };
        }
      } else {
        const decryptedText = await decryptData(encryptedData);
        if (decryptedText) {
          result = {
            decryptedData: decryptedText
          };
        }
      }

      if (result) {
        const decryptionResult: DecryptionResult = {
          ...result,
          timestamp: new Date().toISOString()
        };
        
        setDecryptionResult(decryptionResult);
        setShowResults(true);
        setEncryptedInput('');
      }
    } catch (error) {
      console.error('Erro na descriptografia:', error);
      toast.error('Erro ao descriptografar dados - verifique o formato');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (activeTab === 'encrypt') {
        setInputText(content);
      } else {
        setEncryptedInput(content);
      }
      toast.success('Arquivo carregado com sucesso');
    };
    reader.readAsText(file);
  };

  const downloadResult = (data: string, filename: string) => {
    const blob = new Blob([data], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Arquivo baixado com sucesso');
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado para a área de transferência`);
    } catch (error) {
      toast.error('Erro ao copiar para a área de transferência');
    }
  };

  const useMyPublicKey = () => {
    if (publicKeys.kyber) {
      setRecipientPublicKey(publicKeys.kyber);
      toast.success('Sua chave pública foi inserida');
    } else {
      toast.error('Chave pública não encontrada');
    }
  };

  const formatDataSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const maskSensitiveData = (data: string, show: boolean) => {
    if (show) return data;
    if (data.length <= 20) return '•'.repeat(data.length);
    return data.substring(0, 10) + '•'.repeat(Math.max(0, data.length - 20)) + data.substring(data.length - 10);
  };

  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Lock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Faça login para acessar a interface de criptografia</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasValidKeys()) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Chaves PQC Necessárias</h3>
            <p className="text-gray-500 mb-4">
              Você precisa gerar suas chaves PQC antes de usar a criptografia
            </p>
            <p className="text-sm text-gray-400">
              Acesse o Gerenciador de Chaves para criar suas chaves Kyber e Dilithium
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Interface de Criptografia PQC
          </CardTitle>
          <CardDescription>
            Criptografe e descriptografe dados usando algoritmos pós-quânticos
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => {
            if (activeTab !== 'encrypt') {
              setActiveTab('encrypt');
            }
          }}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'encrypt'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Lock className="h-4 w-4 inline mr-2" />
          Criptografar
        </button>
        <button
          onClick={() => {
            if (activeTab !== 'decrypt') {
              setActiveTab('decrypt');
            }
          }}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'decrypt'
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Unlock className="h-4 w-4 inline mr-2" />
          Descriptografar
        </button>
      </div>

      {/* Configurações */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="useSignature"
                checked={useSignature}
                onChange={(e) => setUseSignature(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="useSignature" className="text-sm font-medium">
                Usar assinatura digital (Dilithium)
              </label>
              <Info className="h-4 w-4 text-gray-400" />
            </div>
            <Badge variant={useSignature ? 'default' : 'outline'}>
              {useSignature ? 'Criptografia + Assinatura' : 'Apenas Criptografia'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Interface de Criptografia */}
      {activeTab === 'encrypt' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-blue-600" />
              Criptografar Dados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Chave Pública do Destinatário */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chave Pública do Destinatário
              </label>
              <div className="flex gap-2">
                <textarea
                  value={recipientPublicKey}
                  onChange={(e) => setRecipientPublicKey(e.target.value)}
                  placeholder="Cole aqui a chave pública Kyber do destinatário..."
                  className="flex-1 p-3 border border-gray-300 rounded-md font-mono text-xs"
                  rows={3}
                />
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={useMyPublicKey}
                    variant="outline"
                    size="sm"
                    title="Usar minha própria chave pública"
                  >
                    <Key className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Dados para Criptografar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dados para Criptografar
              </label>
              <div className="space-y-2">
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Digite ou cole o texto que deseja criptografar..."
                  className="w-full p-3 border border-gray-300 rounded-md"
                  rows={6}
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".txt,.json,.csv"
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Carregar Arquivo
                    </Button>
                  </div>
                  <span className="text-xs text-gray-500">
                    {inputText.length} caracteres
                  </span>
                </div>
              </div>
            </div>

            {/* Botão de Criptografar */}
            <Button
              onClick={handleEncrypt}
              disabled={isEncrypting || !inputText.trim() || !recipientPublicKey.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isEncrypting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Lock className="h-4 w-4 mr-2" />
              )}
              {isEncrypting ? 'Criptografando...' : 'Criptografar Dados'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Interface de Descriptografia */}
      {activeTab === 'decrypt' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Unlock className="h-5 w-5 text-green-600" />
              Descriptografar Dados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Dados Criptografados */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dados Criptografados
              </label>
              <div className="space-y-2">
                <textarea
                  value={encryptedInput}
                  onChange={(e) => setEncryptedInput(e.target.value)}
                  placeholder="Cole aqui os dados criptografados (formato JSON)..."
                  className="w-full p-3 border border-gray-300 rounded-md font-mono text-xs"
                  rows={8}
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".txt,.json"
                      className="hidden"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      size="sm"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Carregar Arquivo
                    </Button>
                  </div>
                  <span className="text-xs text-gray-500">
                    {encryptedInput.length} caracteres
                  </span>
                </div>
              </div>
            </div>

            {/* Botão de Descriptografar */}
            <Button
              onClick={handleDecrypt}
              disabled={isDecrypting || !encryptedInput.trim()}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isDecrypting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Unlock className="h-4 w-4 mr-2" />
              )}
              {isDecrypting ? 'Descriptografando...' : 'Descriptografar Dados'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {showResults && (encryptionResult || decryptionResult) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {encryptionResult ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Criptografia Concluída
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 text-blue-600" />
                    Descriptografia Concluída
                  </>
                )}
              </div>
              <Button
                onClick={() => setShowResults(false)}
                variant="ghost"
                size="sm"
              >
                ✕
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {encryptionResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Algoritmo:</span>
                    <p className="font-mono">{encryptionResult.algorithm}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Tamanho:</span>
                    <p>{formatDataSize(encryptionResult.size)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Timestamp:</span>
                    <p>{new Date(encryptionResult.timestamp).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <Badge className="bg-green-100 text-green-800">Sucesso</Badge>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dados Criptografados
                  </label>
                  <textarea
                    value={encryptionResult.encryptedData}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md font-mono text-xs bg-gray-50"
                    rows={6}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(encryptionResult.encryptedData, 'Dados criptografados')}
                    variant="outline"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                  <Button
                    onClick={() => downloadResult(
                      encryptionResult.encryptedData, 
                      `encrypted-${new Date().toISOString().split('T')[0]}.json`
                    )}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar
                  </Button>
                </div>
              </div>
            )}

            {decryptionResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Timestamp:</span>
                    <p>{new Date(decryptionResult.timestamp).toLocaleString('pt-BR')}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Tamanho:</span>
                    <p>{formatDataSize(decryptionResult.decryptedData.length)}</p>
                  </div>
                  {decryptionResult.isSignatureValid !== undefined && (
                    <div>
                      <span className="font-medium text-gray-500">Assinatura:</span>
                      <Badge className={decryptionResult.isSignatureValid ? 
                        'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }>
                        {decryptionResult.isSignatureValid ? 'Válida' : 'Inválida'}
                      </Badge>
                    </div>
                  )}
                </div>
                
                {decryptionResult.isSignatureValid === false && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-red-800">Atenção: Assinatura Inválida</span>
                    </div>
                    <p className="text-sm text-red-700 mt-1">
                      Os dados foram descriptografados, mas a assinatura digital não é válida. 
                      Isso pode indicar que os dados foram modificados.
                    </p>
                  </div>
                )}
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Dados Descriptografados
                    </label>
                    <Button
                      onClick={() => setShowSensitiveData(!showSensitiveData)}
                      variant="ghost"
                      size="sm"
                    >
                      {showSensitiveData ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <textarea
                    value={maskSensitiveData(decryptionResult.decryptedData, showSensitiveData)}
                    readOnly
                    className="w-full p-3 border border-gray-300 rounded-md bg-gray-50"
                    rows={6}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(decryptionResult.decryptedData, 'Dados descriptografados')}
                    variant="outline"
                    disabled={!showSensitiveData}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copiar
                  </Button>
                  <Button
                    onClick={() => downloadResult(
                      decryptionResult.decryptedData, 
                      `decrypted-${new Date().toISOString().split('T')[0]}.txt`
                    )}
                    variant="outline"
                    disabled={!showSensitiveData}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informações sobre Algoritmos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Sobre os Algoritmos PQC
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4 text-blue-600" />
                CRYSTALS-Kyber
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                Algoritmo de encapsulamento de chaves resistente a ataques quânticos, 
                usado para estabelecer chaves simétricas seguras.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Baseado em problemas de reticulado</li>
                <li>• Padrão NIST para criptografia pós-quântica</li>
                <li>• Combina segurança quântica com eficiência</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-green-600" />
                CRYSTALS-Dilithium
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                Esquema de assinatura digital pós-quântica que garante 
                autenticidade e integridade dos dados.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Assinaturas compactas e verificação rápida</li>
                <li>• Resistente a ataques de computadores quânticos</li>
                <li>• Padrão NIST para assinaturas digitais</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PQCCryptoInterface;